/**
 * Live preview of a Codira "code-as-ad" variation.
 * Inlines the fixed background SVG and overlays the code text
 * inside the dark card, with optional typing animation.
 */
import { useEffect, useState } from "react";

const KEYWORDS = new Set([
  "const", "let", "var", "function", "return", "if", "else", "import",
  "from", "export", "default", "class", "async", "await", "true", "false",
  "null", "undefined", "new", "for", "while", "in", "of", "def", "print",
]);

function tokenize(line) {
  const stripped = line.trimStart();
  if (stripped.startsWith("//") || stripped.startsWith("#")) {
    return [{ text: line, color: "comment" }];
  }
  const tokens = [];
  const re = /(\s+|"[^"]*"|'[^']*'|`[^`]*`|[A-Za-z_][\w$]*|[{}()[\];,:=.+\-*/<>!&|]|\d+)/g;
  let m;
  while ((m = re.exec(line)) !== null) {
    const t = m[0];
    if (/^\s+$/.test(t))        tokens.push({ text: t, color: "ws" });
    else if (/^["'`]/.test(t))  tokens.push({ text: t, color: "string" });
    else if (/^\d+$/.test(t))   tokens.push({ text: t, color: "num" });
    else if (KEYWORDS.has(t))   tokens.push({ text: t, color: "keyword" });
    else if (/^[{}]$/.test(t))  tokens.push({ text: t, color: "brace" });
    else                         tokens.push({ text: t, color: "ident" });
  }
  return tokens;
}

const COLOR_CLASS = {
  comment: "text-zinc-500",
  string:  "text-zinc-400",
  num:     "text-zinc-300",
  keyword: "text-brand",
  brace:   "text-brand",
  ws:      "",
  ident:   "text-zinc-100",
};

export default function CodePreview({
  template,
  codeLines = [],
  className = "",
  animate = false,
}) {
  const lines = (codeLines && codeLines.length) ? codeLines : ["// edit me"];
  const [typed, setTyped] = useState(animate ? 0 : 99999);
  const totalChars = lines.reduce((a, l) => a + l.length + 1, 0);

  useEffect(() => {
    if (!animate) { setTyped(99999); return; }
    setTyped(0);
    let i = 0;
    const id = setInterval(() => {
      i += 1;
      setTyped(i);
      if (i >= totalChars + 30) clearInterval(id);
    }, 28);
    return () => clearInterval(id);
  }, [animate, totalChars]);

  // Guard: this component only renders code templates. A statement template
  // (no code_box) can transiently arrive as a fallback before a parent's effect
  // sets the right templateId — render nothing rather than crash.
  if (!template || !template.code_box) return null;

  const bg = `/templates_${template.bg_key}.svg`;
  const { width: tw, height: th, code_box: box, font_size: fs, line_height: lh } = template;

  // All sizing as % of template intrinsic width → cqw inside the container.
  const fontCqw = (fs / tw) * 100;
  const lineCqw = (lh / tw) * 100;
  const boxLeft = (box.x / tw) * 100;
  const boxTop  = (box.y / th) * 100;
  const boxW    = (box.width / tw) * 100;
  const boxH    = (box.height / th) * 100;

  // Slice typed characters across lines
  let remaining = typed;
  const renderLines = lines.map((line) => {
    const take = Math.min(line.length, Math.max(0, remaining));
    remaining -= take;
    if (take === line.length) remaining -= 1;
    return { text: line.slice(0, take), complete: take === line.length };
  });
  // Find cursor position: last line where typing is in progress
  let cursorLine = -1;
  for (let i = 0; i < renderLines.length; i++) {
    if (!renderLines[i].complete) { cursorLine = i; break; }
  }
  if (cursorLine === -1) cursorLine = renderLines.length - 1;

  return (
    <div
      className={`relative w-full overflow-hidden bg-gray-100 ${className}`}
      style={{ aspectRatio: `${tw} / ${th}`, containerType: "inline-size" }}
    >
      <img
        src={bg} alt={template.name}
        className="absolute inset-0 w-full h-full select-none pointer-events-none"
        draggable={false}
      />
      <div
        className="absolute font-mono whitespace-pre-wrap break-words overflow-hidden"
        style={{
          left: `${boxLeft}cqw`, top: `${(box.y / tw) * 100}cqw`,
          width: `${boxW}cqw`,   height: `${(box.height / tw) * 100}cqw`,
          fontSize: `${fontCqw}cqw`,
          lineHeight: `${lineCqw}cqw`,
          color: "#f4f4f4",
        }}
      >
        {renderLines.map((l, i) => {
          const tokens = tokenize(l.text);
          const isCursor = i === cursorLine;
          return (
            <div key={i}>
              {tokens.map((t, j) => (
                <span key={j} className={COLOR_CLASS[t.color]}>{t.text}</span>
              ))}
              {isCursor && (
                <span
                  className="inline-block bg-brand animate-pulse align-middle"
                  style={{
                    width: `${fontCqw * 0.55}cqw`,
                    height: `${fontCqw}cqw`,
                    marginLeft: `${fontCqw * 0.05}cqw`,
                  }}
                />
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
